package dev.wyns.ventrova.ui.android

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import dev.wyns.ventrova.app.AppUiState
import dev.wyns.ventrova.app.AppViewModel
import dev.wyns.ventrova.app.PlaceItem
import dev.wyns.ventrova.resources.Res
import dev.wyns.ventrova.resources.favorites
import dev.wyns.ventrova.resources.favorites_empty
import dev.wyns.ventrova.resources.ic_arrow_back
import dev.wyns.ventrova.resources.ic_close
import dev.wyns.ventrova.resources.ic_eco
import dev.wyns.ventrova.resources.ic_add_location_alt
import dev.wyns.ventrova.resources.ic_favorite_filled
import dev.wyns.ventrova.resources.ic_info
import dev.wyns.ventrova.resources.ic_north_west
import dev.wyns.ventrova.resources.ic_trending_up
import dev.wyns.ventrova.resources.loading
import dev.wyns.ventrova.resources.filter_all_levels
import dev.wyns.ventrova.resources.profile
import dev.wyns.ventrova.resources.profile_about
import dev.wyns.ventrova.resources.profile_about_body
import dev.wyns.ventrova.resources.profile_default_filter
import dev.wyns.ventrova.resources.profile_help_map_grow
import dev.wyns.ventrova.resources.search_no_results
import dev.wyns.ventrova.resources.search_placeholder
import dev.wyns.ventrova.resources.search_popular_title
import dev.wyns.ventrova.resources.search_results
import dev.wyns.ventrova.resources.search_suggest_one
import dev.wyns.ventrova.resources.suggest_place
import dev.wyns.ventrova.ui.components.LevelTypeDistanceLine
import dev.wyns.ventrova.ui.components.OpenStatusLine
import dev.wyns.ventrova.ui.components.PlaceListSkeleton
import dev.wyns.ventrova.ui.components.PlaceRowSkeleton
import dev.wyns.ventrova.ui.components.PlaceThumbnail
import dev.wyns.ventrova.ui.components.VIcon
import dev.wyns.ventrova.ui.components.bodyStyle
import dev.wyns.ventrova.ui.theme.SearchSuggestions
import dev.wyns.ventrova.ui.theme.VentrovaColors
import dev.wyns.ventrova.ui.theme.displayStyle
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource

private val NavBarSpace = 72.dp

@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun AndroidSearchPage(state: AppUiState, viewModel: AppViewModel, onBack: () -> Unit, onSuggest: () -> Unit) {
    val focus = remember { FocusRequester() }
    LaunchedEffect(Unit) { focus.requestFocus() }
    Column(Modifier.fillMaxSize().background(VentrovaColors.Canvas).statusBarsPadding().navigationBarsPadding().padding(bottom = NavBarSpace)) {
        Row(Modifier.fillMaxWidth().height(72.dp).padding(horizontal = 8.dp), verticalAlignment = Alignment.CenterVertically) {
            IconCircle(Res.drawable.ic_arrow_back, onBack)
            Box(Modifier.weight(1f)) {
                if (state.query.isEmpty()) {
                    Text(stringResource(Res.string.search_placeholder), style = bodyStyle(17.sp, color = VentrovaColors.Subtle))
                }
                BasicTextField(
                    value = state.query,
                    onValueChange = viewModel::setQuery,
                    singleLine = true,
                    textStyle = bodyStyle(17.sp),
                    cursorBrush = SolidColor(VentrovaColors.Accent),
                    modifier = Modifier.fillMaxWidth().focusRequester(focus),
                )
            }
            if (state.query.isNotEmpty()) IconCircle(Res.drawable.ic_close) { viewModel.setQuery("") }
        }
        HorizontalDivider(color = VentrovaColors.Line)
        if (state.query.isBlank()) {
            Text(
                stringResource(Res.string.search_popular_title),
                style = bodyStyle(14.sp, FontWeight.SemiBold, VentrovaColors.Accent),
                modifier = Modifier.padding(start = 20.dp, end = 20.dp, top = 20.dp, bottom = 12.dp),
            )
            FlowRow(Modifier.padding(horizontal = 20.dp), horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                SearchSuggestions.forEach { suggestion ->
                    Row(
                        Modifier
                            .height(34.dp)
                            .clip(RoundedCornerShape(10.dp))
                            .border(1.dp, VentrovaColors.Outline, RoundedCornerShape(10.dp))
                            .clickable { viewModel.setQuery(suggestion) }
                            .padding(start = 10.dp, end = 14.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(6.dp),
                    ) {
                        VIcon(Res.drawable.ic_trending_up, size = 18.dp, tint = VentrovaColors.Secondary)
                        Text(suggestion, style = bodyStyle(14.sp, FontWeight.Medium))
                    }
                }
            }
        } else {
            Text(
                pluralStringResource(Res.plurals.search_results, state.searchResults.size, state.searchResults.size),
                style = bodyStyle(14.sp, color = VentrovaColors.Secondary),
                modifier = Modifier.padding(start = 20.dp, end = 20.dp, top = 16.dp, bottom = 4.dp),
            )
            if (state.isSearching && state.searchResults.isEmpty()) {
                PlaceListSkeleton(count = 4, loadingLabel = stringResource(Res.string.loading)) { index -> AndroidRowSkeleton(index, thumbSize = 48.dp, lines = 1) }
            } else if (state.searchResults.isEmpty()) {
                Row(Modifier.padding(horizontal = 20.dp, vertical = 24.dp)) {
                    Text(stringResource(Res.string.search_no_results) + " ", style = bodyStyle(15.sp, color = VentrovaColors.Secondary))
                    Text(
                        stringResource(Res.string.search_suggest_one),
                        style = bodyStyle(15.sp, FontWeight.SemiBold, VentrovaColors.Accent),
                        modifier = Modifier.clickable(onClick = onSuggest),
                    )
                }
            }
            LazyColumn {
                items(state.searchResults, key = { it.id }) { item ->
                    Row(
                        Modifier.fillMaxWidth().clickable { viewModel.openDetail(item.id) }.padding(horizontal = 20.dp, vertical = 10.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(16.dp),
                    ) {
                        PlaceThumbnail(item.place, Modifier.size(48.dp), RoundedCornerShape(14.dp), iconSize = 22.dp)
                        Column(Modifier.weight(1f)) {
                            Text(item.place.name, style = bodyStyle(16.sp, FontWeight.SemiBold))
                            LevelTypeDistanceLine(item, Modifier.padding(top = 2.dp), style = bodyStyle(13.5.sp, color = VentrovaColors.Secondary), showCuisineInsteadOfCategory = true)
                        }
                        VIcon(Res.drawable.ic_north_west, size = 22.dp, tint = VentrovaColors.Subtle)
                    }
                }
            }
        }
    }
}

@Composable
internal fun AndroidFavoritesPage(state: AppUiState, viewModel: AppViewModel) {
    LazyColumn(
        Modifier.fillMaxSize().background(VentrovaColors.Canvas),
        contentPadding = PaddingValues(start = 16.dp, end = 16.dp, bottom = NavBarSpace + 24.dp),
        verticalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        item {
            Text(
                stringResource(Res.string.favorites),
                style = bodyStyle(32.sp, FontWeight.Medium),
                modifier = Modifier.statusBarsPadding().padding(start = 4.dp, top = 56.dp, bottom = 10.dp),
            )
        }
        if (state.favoritesLoading) {
            items(3) { index ->
                Box(Modifier.fillMaxWidth().clip(RoundedCornerShape(24.dp)).background(VentrovaColors.Sand).padding(12.dp)) {
                    PlaceRowSkeleton(thumbSize = 80.dp, thumbShape = RoundedCornerShape(18.dp), spacing = 14.dp, seed = index)
                }
            }
        } else if (state.favorites.isEmpty()) {
            item {
                Text(
                    stringResource(Res.string.favorites_empty),
                    style = bodyStyle(15.sp, color = VentrovaColors.Secondary),
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 24.dp, vertical = 48.dp),
                )
            }
        }
        items(state.favorites, key = { it.id }) { item -> FavoriteCard(item, viewModel) }
    }
}

@Composable
private fun FavoriteCard(item: PlaceItem, viewModel: AppViewModel) {
    Row(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(24.dp))
            .background(VentrovaColors.Sand)
            .clickable { viewModel.openDetail(item.id) }
            .padding(12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(14.dp),
    ) {
        PlaceThumbnail(item.place, Modifier.size(80.dp), RoundedCornerShape(18.dp), iconSize = 28.dp)
        Column(Modifier.weight(1f)) {
            Text(item.place.name, style = displayStyle(18, lineHeight = 1.15f))
            LevelTypeDistanceLine(item, Modifier.padding(top = 3.dp), style = bodyStyle(13.5.sp, color = VentrovaColors.Secondary), showCategory = false)
            OpenStatusLine(item, Modifier.padding(top = 3.dp), style = bodyStyle(13.5.sp, color = VentrovaColors.Secondary))
        }
        Box(
            Modifier.size(44.dp).clip(CircleShape).clickable { viewModel.toggleFavorite(item.id) },
            contentAlignment = Alignment.Center,
        ) {
            VIcon(Res.drawable.ic_favorite_filled, tint = VentrovaColors.Favorite)
        }
    }
}

@Composable
internal fun AndroidProfilePage(onSuggest: () -> Unit, onNotAvailable: () -> Unit) {
    Column(Modifier.fillMaxSize().background(VentrovaColors.Canvas).statusBarsPadding().padding(horizontal = 16.dp)) {
        Text(stringResource(Res.string.profile), style = bodyStyle(32.sp, FontWeight.Medium), modifier = Modifier.padding(start = 4.dp, top = 56.dp, bottom = 20.dp))
        Column(Modifier.fillMaxWidth().clip(RoundedCornerShape(24.dp)).background(VentrovaColors.Sand)) {
            ProfileRow(Res.drawable.ic_eco, stringResource(Res.string.profile_default_filter), stringResource(Res.string.filter_all_levels), onNotAvailable)
            ProfileRow(Res.drawable.ic_add_location_alt, stringResource(Res.string.suggest_place), stringResource(Res.string.profile_help_map_grow), onSuggest)
            ProfileRow(Res.drawable.ic_info, stringResource(Res.string.profile_about), stringResource(Res.string.profile_about_body), null)
        }
    }
}

@Composable
private fun ProfileRow(icon: DrawableResource, title: String, subtitle: String, onClick: (() -> Unit)?) {
    Row(
        Modifier.fillMaxWidth().let { if (onClick != null) it.clickable(onClick = onClick) else it }.padding(horizontal = 20.dp, vertical = 16.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(16.dp),
    ) {
        VIcon(icon, tint = VentrovaColors.Secondary)
        Column(Modifier.weight(1f)) {
            Text(title, style = bodyStyle(16.sp))
            Text(subtitle, style = bodyStyle(14.sp, color = VentrovaColors.Secondary))
        }
    }
}

@Composable
internal fun IconCircle(icon: DrawableResource, onClick: () -> Unit) {
    Box(Modifier.size(48.dp).clip(CircleShape).clickable(onClick = onClick), contentAlignment = Alignment.Center) {
        VIcon(icon, tint = VentrovaColors.Ink)
    }
}
